import React from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Trophy,
  Heart,
  Zap,
  RotateCcw,
  Star,
  Building2,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { clinicData } from "../mock";
import { useNavigate } from "react-router-dom";

const ServicesSection = () => {
  const iconMap = {
    trophy:
      "./spr.jpg",
    heart:
      "/prm.webp",
    spine:
      "cc.webp",
    refresh:
      "rhb.jpg",
    star: "./cwp.png",
    building:
      "./cw.avif",
  };

  const navigate = useNavigate();
  const handleBookAppointment = (serviceTitle) => {
    navigate("/book-appointment", {
      state: { selectedService: serviceTitle || "" },
    });
  };

  return (
    <section
      id="services"
      className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#204080]/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 bg-[#204080]/10 text-[#204080] px-5 py-3 rounded-full text-base font-semibold mb-6">
            <Heart size={20} />
            <span>Our Services</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#204080] to-[#3a5bb8] drop-shadow-sm mb-4 tracking-tight leading-snug font-serif">
            Comprehensive Care Tailored Solutions
            {/* <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
              Tailored Solutions
            </span> */}
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-serif">
            From injury recovery to wellness optimization, our specialized
            services are designed to meet your unique healthcare needs with
            precision and care.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {clinicData.services.map((service) => {
            const imgSrc = iconMap[service.icon];

            return (
              <Card
                key={service.id}
                className="group relative overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              >
                {/* Glassmorphic Background (visual only, non-interactive) */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm"></div>

                {/* Visual overlay (DO NOT capture pointer events) */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#204080]/5 to-[#2E5AAC]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                {/* Content (above overlay) */}
                <div className="relative p-8 space-y-6 z-20">
                  {/* Icon */}
                  <div className="relative">
                    {/* Image */}
                    <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-lg">
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                    </div>

                    {/* <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br from-amber-300 to-amber-500 rounded-full shadow-lg group-hover:scale-125 transition-transform duration-300"></div> */}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-[#204080] group-hover:text-[#2E5AAC] transition-colors duration-300">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">
                      Key Features:
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle
                            size={14}
                            className="text-[#2E5AAC] flex-shrink-0"
                          />
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      className="inline-flex items-center space-x-2 text-[#204080] hover:text-[#2E5AAC] font-semibold transition-colors duration-300 group"
                      onClick={() => {
                        if (service.title === "Sports Injury Recovery")
                          navigate("/sports-injury-recovery");
                        else if (service.title === "Pain Relief & Management")
                          navigate("/pain-relief-management");
                        else if (service.title === "Chiropractic Care")
                          navigate("/chiropractic-care");
                        else if (service.title === "Rehabilitation")
                          navigate("/rehabilitation");
                        else if (
                          service.title === "Customized Wellness Programs"
                        )
                          navigate("/customized-wellness-programs");
                        else if (service.title === "Corporate Wellness")
                          navigate("/corporate-wellness");
                      }}
                    >
                      <span>Learn More</span>
                      {/* animate on button hover using local group */}
                      <ArrowRight
                        size={16}
                        className="group-hover:translate-x-1 transition-transform duration-200"
                      />
                    </button>
                  </div>
                </div>

                {/* Bottom Accent (visual only) */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#204080] to-[#2E5AAC] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left pointer-events-none"></div>
              </Card>
            );
          })}
        </div>

        {/* Service Highlights */}
        <div className="mt-20 grid md:grid-cols-3 gap-8">
          <Card className="p-8 bg-gradient-to-br from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl">
            <div className="text-center space-y-4">
              <div className="text-4xl font-bold">98%</div>
              <div className="text-blue-100">Patient Satisfaction Rate</div>
              <p className="text-sm text-blue-200">
                Consistently rated as the top physiotherapy clinic in the region
              </p>
            </div>
          </Card>

          <Card className="p-8 bg-white/90 backdrop-blur-sm border border-[#204080]/20 shadow-xl">
            <div className="text-center space-y-4">
              <div className="text-4xl font-bold text-[#204080]">24/7</div>
              <div className="text-gray-700">Emergency Support</div>
              <p className="text-sm text-gray-600">
                Round-the-clock assistance for urgent care needs
              </p>
            </div>
          </Card>

          <Card className="p-8 bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0 shadow-2xl">
            <div className="text-center space-y-4">
              <div className="text-4xl font-bold">Fast</div>
              <div className="text-teal-100">Same-Day Appointments</div>
              <p className="text-sm text-teal-200">
                Quick access to care when you need it most
              </p>
            </div>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <Card className="max-w-4xl mx-auto p-8 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-2xl">
            <h3 className="text-2xl font-bold text-[#204080] mb-4">
              Ready to Start Your Wellness Journey?
            </h3>
            <p className="text-gray-600 mb-6 text-lg">
              Choose the service that best fits your needs, or schedule a
              comprehensive consultation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                onClick={() => navigate("/book-appointment")}
              >
                Schedule Consultation
              </button>
              <button
                className="px-8 py-3 border-2 border-[#204080] text-[#204080] hover:bg-[#204080] hover:text-white font-semibold rounded-lg transition-all duration-300"
                onClick={() => navigate("/services")}
              >
                View All Services
              </button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
